import { useEffect, useRef, useState } from "react";
import { BarChart3, LayoutGrid, MessageCircle, Wallet, Receipt, Users, Settings as SettingsIcon, Briefcase, ChevronRight } from "lucide-react";

/**
 * ToothmatrixTour — sticky-scrolling product showcase, Stripe/Linear-style.
 *
 * Visual model: on the LEFT, a tall column with one chapter card per screen; on
 * the RIGHT, a single sticky image area that swaps as you scroll past each
 * chapter. Behaves on mobile as a simple stacked timeline (no stickiness).
 *
 * Re-uses the 17 screenshots already captured into /api/static/screenshots/
 * by /app/backend/capture_docs_screenshots.py.
 */

const API_HOST = process.env.REACT_APP_BACKEND_URL;
const shot = (file) => `${API_HOST}/api/static/screenshots/${file}`;
const vidMp4 = (file) => `${API_HOST}/api/static/videos/${file}`;
const vidWebm = (file) => `${API_HOST}/api/static/videos/${file.replace(/\.mp4$/, ".webm")}`;

const CHAPTERS = [
    {
        id: "dashboard",
        icon: BarChart3,
        eyebrow: "Live Dashboard",
        title: "Your lab's heartbeat in one screen.",
        body: "Live KPIs · late-case chips · technician workload · drill-down drawers on every tile. Auto-balance the day with one click when a tech goes overloaded.",
        bullets: ["Cases today · MTD · YTD", "Late-SLA chips, automatic", "Tech workload + WIP limits", "Drill into any number"],
        img: "10_admin_dashboard.png",
        video: "dashboard.mp4",
    },
    {
        id: "kanban",
        icon: LayoutGrid,
        eyebrow: "Cases · Kanban",
        title: "Drag cards to change status. Done.",
        body: "Six columns from Received → Delivered. Drag a card across columns to advance status. Amber chips warn when a column hits the WIP limit you set in Settings.",
        bullets: ["Real-time status updates", "Drag-and-drop reassignment", "Amber over-WIP warning", "One-click switch to table view"],
        img: "21_admin_cases_kanban.png",
        video: "kanban.mp4",
    },
    {
        id: "case-detail",
        icon: MessageCircle,
        eyebrow: "Case Detail",
        title: "Every conversation. Every status change. One feed.",
        body: "Chat with the dentist · attach drawings · view the 3D STL · and below it all, a Salesforce-style activity timeline that's the canonical audit trail.",
        bullets: ["In-case chat with drawing annotations", "3D STL viewer", "Activity timeline (status · chat · invoices)", "Print warranty + shipping label"],
        img: "27_case_detail_with_activity.png",
        video: "case-detail.mp4",
    },
    {
        id: "invoices",
        icon: Receipt,
        eyebrow: "Invoices & Payments",
        title: "Invoicing on autopilot.",
        body: "Every case auto-generates an invoice. Stripe-paid invoices clear instantly. Prepaid-credit clinics settle automatically the moment a case is accepted.",
        bullets: ["Auto-generated per case", "Stripe + Razorpay + bank transfer", "Auto-settle from credit balance", "Mark-paid in one click"],
        img: "25_admin_invoices.png",
        video: "invoices.mp4",
    },
    {
        id: "finance",
        icon: Briefcase,
        eyebrow: "Finance & Payroll",
        title: "Run your books without a separate ERP.",
        body: "Expenses · employees · monthly payroll runs · branded payslip PDFs · bank-export CSVs · year-end Form 16 + salary register. All in one tab.",
        bullets: ["Expense categories + receipt upload", "Monthly payroll with adjustments", "Payslip PDFs with QR verify", "Bank-format export"],
        img: "24_admin_finance.png",
        video: "finance.mp4",
    },
    {
        id: "team",
        icon: Users,
        eyebrow: "Team · UMS",
        title: "One checkbox per permission.",
        body: "Six built-in roles · every permission a checkbox · 'View-as' preview to see exactly what each role gets before you assign them.",
        bullets: ["Roles: Tech · Lead Tech · Finance · Sales · Employee", "Permissions: 30+ granular keys", "View-as live preview", "Audit-logged every change"],
        img: "22_admin_team_ums.png",
        video: "team.mp4",
    },
    {
        id: "settings",
        icon: SettingsIcon,
        eyebrow: "Settings",
        title: "Everything you'd ever need to configure.",
        body: "SMTP · Twilio + CallMeBot WhatsApp · Stripe + Razorpay · Currency · GST · Kanban WIP limit · scheduled jobs · daily snapshots. All here.",
        bullets: ["Email & WhatsApp delivery", "Payment providers", "Currency · GST · WIP limit", "Cron schedules"],
        img: "23_admin_settings.png",
        video: "settings.mp4",
    },
    {
        id: "credits",
        icon: Wallet,
        eyebrow: "Prepaid Credits",
        title: "Clinics pay once. You stop chasing AR.",
        body: "Clinics top up via Stripe. Cases auto-settle from their credit float on acceptance. Set thresholds for off-session auto-top-up, eliminate AR follow-ups.",
        bullets: ["One-time top-up via Stripe", "Auto-settle invoices from credits", "Auto-top-up via SetupIntent", "Per-clinic ledger + reconciliation"],
        img: "30_preview_finance.png",
        video: "credits.mp4",
    },
];

export default function ToothmatrixTour() {
    const [activeIdx, setActiveIdx] = useState(0);
    const chapterRefs = useRef([]);
    const videoRefs = useRef([]);
    const mobileVideoRefs = useRef([]);

    useEffect(() => {
        const observers = chapterRefs.current.map((el, idx) => {
            if (!el) return null;
            const obs = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.5) setActiveIdx(idx);
                },
                { threshold: [0.5], rootMargin: "-25% 0px -25% 0px" },
            );
            obs.observe(el);
            return obs;
        });
        return () => observers.forEach((o) => o?.disconnect());
    }, []);

    // Play only the active sticky-stack video. autoplay is brittle; control it imperatively.
    useEffect(() => {
        videoRefs.current.forEach((v, i) => {
            if (!v) return;
            if (i === activeIdx) {
                // Ensure the source is loaded (it may have preload="none")
                if (v.readyState === 0) {
                    try { v.load(); } catch { /* noop */ }
                }
                const tryPlay = () => {
                    v.currentTime = 0;
                    const p = v.play();
                    if (p && typeof p.catch === "function") {
                        p.catch(() => { /* autoplay blocked by browser policy — poster shows */ });
                    }
                };
                if (v.readyState >= 2) {
                    tryPlay();
                } else {
                    const onReady = () => { tryPlay(); v.removeEventListener("loadeddata", onReady); };
                    v.addEventListener("loadeddata", onReady);
                }
            } else {
                v.pause();
            }
        });
    }, [activeIdx]);

    const active = CHAPTERS[activeIdx];

    return (
        <section className="relative py-24 bg-slate-50 border-t border-slate-200" data-testid="toothmatrix-tour">
            <div className="max-w-6xl mx-auto px-6 sm:px-8 mb-14">
                <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700 mb-3">· Product tour</div>
                <h2 className="text-3xl sm:text-4xl font-display tracking-tight max-w-3xl leading-tight">Eight screens. Forty-five seconds. See what your lab will run on.</h2>
                <p className="mt-3 text-slate-600 max-w-2xl text-[15px] leading-relaxed">Scroll. Each chapter swaps the screen on the right. Or jump straight to a chapter with the buttons below.</p>

                {/* Chapter dots — quick jump */}
                <div className="mt-6 flex flex-wrap gap-2">
                    {CHAPTERS.map((c, i) => (
                        <button
                            key={c.id}
                            onClick={() => {
                                setActiveIdx(i);
                                chapterRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            data-testid={`tour-jump-${c.id}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition ${
                                i === activeIdx
                                    ? "bg-[#0B1120] text-white"
                                    : "bg-white border border-slate-200 text-slate-700 hover:border-cyan-300"
                            }`}
                        >
                            <c.icon size={11} /> {c.eyebrow}
                        </button>
                    ))}
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-[1.05fr_1.4fr] gap-10 items-start">
                {/* LEFT: chapter cards */}
                <div className="space-y-12 lg:space-y-32">
                    {CHAPTERS.map((c, i) => (
                        <div
                            key={c.id}
                            ref={(el) => (chapterRefs.current[i] = el)}
                            data-testid={`tour-chapter-${c.id}`}
                            className="scroll-mt-32"
                        >
                            <div className={`transition-opacity duration-300 ${i === activeIdx ? "opacity-100" : "opacity-50"}`}>
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-800 text-[11px] font-mono-tr tracking-wide mb-3">
                                    <c.icon size={11} /> {String(i + 1).padStart(2, "0")} · {c.eyebrow}
                                </div>
                                <h3 className="text-2xl sm:text-3xl font-display tracking-tight leading-tight">{c.title}</h3>
                                <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">{c.body}</p>
                                <ul className="mt-4 space-y-1.5">
                                    {c.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-[13.5px] text-slate-700">
                                            <ChevronRight size={13} className="text-cyan-600 mt-1 shrink-0" /> {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Mobile: show video inline below each chapter (autoplays when scrolled into view) */}
                            <div className="lg:hidden mt-5 rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-md">
                                <video
                                    ref={(el) => (mobileVideoRefs.current[i] = el)}
                                    poster={shot(c.img)}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    className="w-full h-auto block"
                                    data-testid={`tour-video-mobile-${c.id}`}
                                >
                                    <source src={vidWebm(c.video)} type="video/webm" />
                                    <source src={vidMp4(c.video)} type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    ))}
                </div>

                {/* RIGHT: sticky single image area (desktop only) */}
                <div className="hidden lg:block sticky top-24 self-start">
                    <div className="relative rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-2xl shadow-cyan-900/10">
                        {/* Browser chrome */}
                        <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5 bg-slate-50">
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                            <span className="ml-3 text-[11px] font-mono-tr text-slate-400 truncate">{active.id}.toothmatrix.com / admin</span>
                        </div>
                        {/* Crossfade video swap. Only the active video plays (see useEffect above). */}
                        <div className="relative aspect-[1600/900] bg-slate-50">
                            {CHAPTERS.map((c, i) => (
                                <video
                                    key={c.id}
                                    ref={(el) => (videoRefs.current[i] = el)}
                                    poster={shot(c.img)}
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    data-testid={`tour-video-${c.id}`}
                                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === activeIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                                >
                                    <source src={vidWebm(c.video)} type="video/webm" />
                                    <source src={vidMp4(c.video)} type="video/mp4" />
                                </video>
                            ))}
                        </div>
                    </div>
                    <div className="mt-3 text-[11.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 text-center">
                        Chapter {String(activeIdx + 1).padStart(2, "0")} of {CHAPTERS.length}
                    </div>
                </div>
            </div>
        </section>
    );
}
